import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  addUiAuditAttachment,
  createUiAuditRule,
  createUiAuditSnapshot,
  deleteUiAuditRule,
  getUiAuditCell,
  getUiAuditMatrix,
  importUiAuditAi,
  listUiAuditSnapshots,
  removeUiAuditAttachment,
  updateUiAuditCell,
  updateUiAuditRule,
  type IImportAiPayload,
  type IUiAuditFilter,
  type UiAuditCheckMethod,
  type UiAuditSeverity,
  type UiAuditStatus,
} from '@/api/ui-audit.api';

const ROOT_KEY = ['ui-audit'] as const;

export function useUiAuditMatrix(filter: IUiAuditFilter) {
  return useQuery({
    queryKey: [...ROOT_KEY, 'matrix', filter],
    queryFn: () => getUiAuditMatrix(filter),
    staleTime: 5_000,
  });
}

export function useUiAuditCell(ruleId: string | null, screenId: string | null) {
  return useQuery({
    queryKey: [...ROOT_KEY, 'cell', ruleId, screenId],
    queryFn: () => getUiAuditCell(ruleId!, screenId!),
    enabled: Boolean(ruleId && screenId),
  });
}

export function useUpdateUiAuditCell() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: {
      ruleId: string;
      screenId: string;
      dto: { status?: UiAuditStatus; severity?: UiAuditSeverity; notes?: string };
    }) => updateUiAuditCell(args.ruleId, args.screenId, args.dto),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: [...ROOT_KEY, 'matrix'] });
      qc.invalidateQueries({ queryKey: [...ROOT_KEY, 'cell', vars.ruleId, vars.screenId] });
    },
  });
}

export function useCreateUiAuditRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dto: {
      code?: string;
      section: string;
      description: string;
      defaultSeverity: UiAuditSeverity;
      checkMethod: UiAuditCheckMethod;
      note?: string;
    }) => createUiAuditRule(dto),
    onSuccess: () => qc.invalidateQueries({ queryKey: ROOT_KEY }),
  });
}

export function useUpdateUiAuditRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { id: string; dto: Parameters<typeof updateUiAuditRule>[1] }) =>
      updateUiAuditRule(args.id, args.dto),
    onSuccess: () => qc.invalidateQueries({ queryKey: ROOT_KEY }),
  });
}

export function useDeleteUiAuditRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteUiAuditRule(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ROOT_KEY }),
  });
}

export function useImportUiAuditAi() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: IImportAiPayload) => importUiAuditAi(payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ROOT_KEY }),
  });
}

export function useAddUiAuditAttachment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: {
      ruleId: string;
      screenId: string;
      dto: { fileUrl: string; fileName: string; fileSize: number; mimeType: string };
    }) => addUiAuditAttachment(args.ruleId, args.screenId, args.dto),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: [...ROOT_KEY, 'cell', vars.ruleId, vars.screenId] });
    },
  });
}

export function useRemoveUiAuditAttachment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (attachmentId: string) => removeUiAuditAttachment(attachmentId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ROOT_KEY }),
  });
}

export function useUiAuditSnapshots() {
  return useQuery({
    queryKey: [...ROOT_KEY, 'snapshots'],
    queryFn: () => listUiAuditSnapshots(),
  });
}


export function useCreateUiAuditSnapshot() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { dto: { snapshotName: string; description?: string }; filter: IUiAuditFilter }) =>
      createUiAuditSnapshot(args.dto, args.filter),
    onSuccess: () => qc.invalidateQueries({ queryKey: [...ROOT_KEY, 'snapshots'] }),
  });
}
